import { createSignal } from "solid-js";
import { apiBase, votePiece } from "./api";

// Piece likes signal. Persists to localStorage AND POSTs to the server's
// `/pieces/:id/vote` (API33 · I18). Optimistic update + rollback on
// network failure keeps the heart responsive on slow connections.
//
// Storage key is versioned so we can re-shape the payload later without
// inheriting stale data.

const STORAGE_KEY = "ikamiro:likes:v1";

const loadFromStorage = (): Set<string> => {
  if (typeof localStorage === "undefined") return new Set();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x): x is string => typeof x === "string"));
  } catch {
    return new Set();
  }
};

const persist = (set: Set<string>) => {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    // quota or private-mode failure — fail open, in-memory state still works
  }
};

const [likedSet, setLikedSet] = createSignal<Set<string>>(loadFromStorage());

export const likedPieces = likedSet;

export const isLiked = (id: string): boolean => likedSet().has(id);

/** Seed the liked set from server-returned `my_vote=1` rows on first load. */
export const seedLiked = (ids: Iterable<string>): void => {
  const next = new Set(likedSet());
  for (const id of ids) next.add(id);
  setLikedSet(next);
  persist(next);
};

/** Toggle the like for a piece. Optimistic update + rollback on server 4xx.
 *  Returns the new liked-state immediately. */
export const toggleLike = (id: string): boolean => {
  const before = likedSet();
  const willLike = !before.has(id);
  const next = new Set(before);
  if (willLike) next.add(id);
  else next.delete(id);
  setLikedSet(next);
  persist(next);

  void votePiece(id, willLike ? 1 : 0).then((res) => {
    if (!res) {
      setLikedSet(before);
      persist(before);
    }
  });

  return willLike;
};

/** Lightbox dislike control. Clears the heart-liked flag locally and
 *  forwards `value=-1` to the server (the canvas heart only renders
 *  positive votes; dislikes live in the lightbox right-rail). */
export const setDislike = (id: string): void => {
  const before = likedSet();
  if (before.has(id)) {
    const next = new Set(before);
    next.delete(id);
    setLikedSet(next);
    persist(next);
  }
  void votePiece(id, -1);
};

/** Clears any vote (heart or thumb-down). */
export const clearVote = (id: string): void => {
  const before = likedSet();
  if (before.has(id)) {
    const next = new Set(before);
    next.delete(id);
    setLikedSet(next);
    persist(next);
  }
  void votePiece(id, 0);
};

// --- WEB62 · API63 — mirror reconcile on login ---------------------------
//
// The localStorage mirror above is the render source (hearts paint from it
// before the first server response), so reconcile NEVER clears it — the
// server dedupes by (piece_id, voter_id) and skips already-voted ids, which
// makes replay safe without touching the mirror.

/** Ordered mirror of liked piece ids, oldest dropped past 500. */
export const getMirroredVoteIds = (): string[] => [...likedSet()].slice(-500);

export type ReconcileResult = {
  reconciled: string[];
  skipped: { id: string; reason: string }[];
};

let reconcileDone = false;
let reconcileInflight: Promise<ReconcileResult | null> | null = null;

/** POST the mirror to `POST /me/votes/reconcile` once per session.
 *  No-op (null) when logged out, when the mirror is empty, or when a
 *  previous call in this session already converged. A `rate_capped`
 *  skip leaves the gate open so the next login retries the tail. */
export const reconcileVotes = (): Promise<ReconcileResult | null> => {
  if (reconcileDone) return Promise.resolve(null);
  if (reconcileInflight) return reconcileInflight;
  const ids = getMirroredVoteIds();
  if (ids.length === 0) {
    reconcileDone = true;
    return Promise.resolve(null);
  }
  reconcileInflight = (async (): Promise<ReconcileResult | null> => {
    try {
      const res = await fetch(`${apiBase}/me/votes/reconcile`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) return null;
      const body = (await res.json()) as ReconcileResult;
      const capped = (body.skipped ?? []).some((s) => s.reason === "rate_capped");
      if (!capped) reconcileDone = true;
      return body;
    } catch {
      return null;
    } finally {
      reconcileInflight = null;
    }
  })();
  return reconcileInflight;
};
